/**
 * Document type configuration and constants
 */

import {
  FileText,
  Briefcase,
  Home,
  Shield,
  Gavel,
  Mail,
  CreditCard,
  Receipt,
  FileQuestion,
  ScrollText,
  Heart,
  type LucideIcon
} from 'lucide-react';

import {
  acceptStringFor,
  uploadableMimeTypesFor
} from '@/lib/constants/file-type-accept';

export interface FileTypeConfig {
  label: string;
  icon: LucideIcon;
  category: string;
  acceptedExtensions?: string[];
  maxSizeBytes?: number;
}

/**
 * File type configuration mapping
 * Maps backend FileCategory enum values to display configuration
 */
export const FILE_TYPE_CONFIG: Record<string, FileTypeConfig> = {
  WILL: {
    label: 'Will',
    icon: ScrollText, // Distinctive scroll icon for wills
    category: 'legal',
    acceptedExtensions: ['pdf', 'doc', 'docx'],
    maxSizeBytes: 20 * 1024 * 1024 // 20MB
  },
  DEATH_CERTIFICATE: {
    label: 'Death Certificate',
    icon: Heart, // Heart icon to represent life/death documents
    category: 'legal',
    acceptedExtensions: ['pdf', 'jpg', 'jpeg', 'png'],
    maxSizeBytes: 20 * 1024 * 1024
  },
  PROBATE_APPLICATION: {
    label: 'Probate Application',
    icon: Briefcase,
    category: 'legal',
    acceptedExtensions: ['pdf', 'doc', 'docx'],
    maxSizeBytes: 20 * 1024 * 1024
  },
  PROPERTY_DOCUMENT: {
    label: 'Property Document',
    icon: Home,
    category: 'asset',
    acceptedExtensions: ['pdf', 'doc', 'docx'],
    maxSizeBytes: 20 * 1024 * 1024 // 20MB for property docs
  },
  SHARE_CERTIFICATE: {
    label: 'Share Certificate',
    icon: Receipt,
    category: 'financial',
    acceptedExtensions: ['pdf', 'jpg', 'jpeg', 'png'],
    maxSizeBytes: 20 * 1024 * 1024
  },
  INSURANCE_DOCUMENT: {
    label: 'Insurance Document',
    icon: Shield,
    category: 'financial',
    acceptedExtensions: ['pdf', 'doc', 'docx'],
    maxSizeBytes: 20 * 1024 * 1024
  },
  COURT_DOCUMENT: {
    label: 'Court Document',
    icon: Gavel,
    category: 'legal',
    acceptedExtensions: ['pdf'],
    maxSizeBytes: 20 * 1024 * 1024
  },
  CORRESPONDENCE: {
    label: 'Correspondence',
    icon: Mail,
    category: 'communication',
    acceptedExtensions: ['pdf', 'doc', 'docx', 'msg', 'eml'],
    maxSizeBytes: 20 * 1024 * 1024
  },
  IDENTIFICATION: {
    label: 'Identification',
    icon: CreditCard,
    category: 'personal',
    acceptedExtensions: ['pdf', 'jpg', 'jpeg', 'png'],
    maxSizeBytes: 5 * 1024 * 1024 // 5MB for ID docs
  },
  STATEMENT: {
    label: 'Statement',
    icon: FileText,
    category: 'financial',
    acceptedExtensions: ['pdf', 'csv', 'xls', 'xlsx'],
    maxSizeBytes: 20 * 1024 * 1024
  },
  APPLICATION_FORM: {
    label: 'Application Form',
    icon: FileText,
    category: 'legal',
    acceptedExtensions: ['pdf', 'doc', 'docx'],
    maxSizeBytes: 20 * 1024 * 1024
  },
  OTHER: {
    label: 'Other',
    icon: FileQuestion,
    category: 'other',
    acceptedExtensions: ['*'], // Accept all
    maxSizeBytes: 20 * 1024 * 1024
  }
};

/**
 * Get file type options for select/dropdown
 */
export function getFileTypeOptions() {
  return Object.entries(FILE_TYPE_CONFIG).map(([value, config]) => ({
    value,
    label: config.label,
    icon: config.icon,
    category: config.category
  }));
}

/**
 * Get file types by category
 */
export function getFileTypesByCategory(category: string) {
  return Object.entries(FILE_TYPE_CONFIG)
    .filter(([_, config]) => config.category === category)
    .map(([value, config]) => ({
      value,
      ...config
    }));
}

/**
 * File upload constraints for the matter "Upload Additional Documents" dialog.
 *
 * **The format list is derived, not authored (VALE-1150).** Everything uploaded through this
 * dialog is stored as an `additional-document`, so what it may offer is that type's contract and
 * nothing else. The list was hand-kept until now and had drifted exactly the way every other
 * copy did: it advertised `image/gif`, `text/csv`, `text/plain` and a legacy
 * `application/vnd.ms-excel`, none of which `additional-document` has ever accepted. A user
 * picking a GIF or a CSV here passed the picker, passed every client-side check, and was refused
 * after the S3 PUT — the same failure VALE-209 fixed elsewhere and could not see here, because
 * its reconciliation test only reached the drop-zone presets and the two `FILE_TYPE_METADATA`
 * registries.
 *
 * The forwarded-email formats (LF-6125) survive the change and are still derived rather than
 * listed: `additional-document` sets `allowsEmailAttachmentExtraction`, so the unwrapping set
 * and the `.eml`/`.msg` extension fallbacks both come from the contract.
 */
export const FILE_UPLOAD_CONSTRAINTS = {
  maxFileSize: 20 * 1024 * 1024, // 20MB
  /** The MIME types a file may report, for the pre-upload check. */
  acceptedMimeTypes: uploadableMimeTypesFor('additional-document'),
  /**
   * The native `<input accept>` string. Not `acceptedMimeTypes.join(',')`: that set includes the
   * generic `application/octet-stream` a real `.msg` arrives under, which as a picker filter
   * would match nearly every unlabelled file on the disk.
   */
  accept: acceptStringFor('additional-document')
};

/**
 * Human-readable summary of `FILE_UPLOAD_CONSTRAINTS.accept`, for hint text on the
 * matter "Upload Additional Documents" dialog and its empty-state CTA — the single place those
 * two surfaces describe what they accept, so they can't drift apart or omit a format the dialog
 * genuinely takes (VALE-712 — Outlook messages were accepted here but never advertised).
 */
export const ADDITIONAL_DOCUMENT_ACCEPTED_FORMATS_HINT =
  'PDF, Word, JPG, PNG, or a forwarded email (.eml/.msg)';

/**
 * Document categories for filtering
 */
export const DOCUMENT_CATEGORIES = [
  { value: 'all', label: 'All Documents' },
  { value: 'legal', label: 'Legal Documents' },
  { value: 'financial', label: 'Financial Documents' },
  { value: 'asset', label: 'Asset Documents' },
  { value: 'personal', label: 'Personal Documents' },
  { value: 'communication', label: 'Communications' },
  { value: 'other', label: 'Other' }
] as const;

export type DocumentCategory = (typeof DOCUMENT_CATEGORIES)[number]['value'];
